import { useCallback, useEffect, useRef, useState } from "react";

import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";

export const useEnsureProfile = () => {
  // Only subscribe to id - get fetchProfile via getState() to avoid dep issues
  const id = useProfileStore((state) => state.id);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const mountedRef = useRef(true);

  // Function to force a profile refresh
  const refreshProfile = useCallback(async () => {
    if (isLoading) return; // Prevent concurrent refreshes

    setIsLoading(true);
    setError(null);

    try {
      // Check if we have a session
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }

      if (!sessionData.session) {
        throw new Error("No active session found");
      }

      // Force profile fetch from the store - get via getState() to avoid deps
      const { fetchProfile } = useProfileStore.getState();
      await fetchProfile();

      // After fetch, check if we have an ID
      const currentId = useProfileStore.getState().id;

      if (!currentId && retryCount < maxRetries) {
        console.log("Profile ID still not available, will retry");
        if (mountedRef.current) {
          setRetryCount((prev) => prev + 1);

          // Retry with exponential backoff
          setTimeout(
            () => {
              if (mountedRef.current) {
                refreshProfile();
              }
            },
            500 * Math.pow(2, retryCount)
          );
        }
      } else if (!currentId) {
        throw new Error(`Failed to load profile after ${maxRetries} attempts`);
      } else {
        if (mountedRef.current) {
          setIsLoading(false);
          setRetryCount(0);
        }
      }
    } catch (err) {
      console.error("Error refreshing profile:", err);
      if (mountedRef.current) {
        setError(
          err instanceof Error ? err.message : "Failed to load user profile"
        );
        setIsLoading(false);
      }
    }
  }, [isLoading, retryCount]); // Removed fetchProfile - using getState() inside

  // Use ref to avoid refreshProfile in dependency array (it changes with isLoading/retryCount)
  const refreshProfileRef = useRef(refreshProfile);
  refreshProfileRef.current = refreshProfile;

  // Check for profile on mount
  useEffect(() => {
    mountedRef.current = true;

    if (!id) {
      refreshProfileRef.current();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [id]); // Removed refreshProfile - using ref instead

  return {
    isProfileLoaded: !!id,
    isLoading,
    error,
    refreshProfile,
  };
};
